import { useState } from 'react';
import { ExternalLink, FileLock2 } from 'lucide-react';
import { ActionStack, Screen } from '@/app/layout/Screen';
import { Button } from '@/components/ui/Button';
import { Kicker, Lead, MiniIcon, PageTitle, Panel, Row, RowList } from '@/components/ui/Surface';
import { miniappApi } from '@/api/miniapp/miniappApi';
import { useAuthStore } from '@/store/authStore';
import { getBridge } from '@/platform';
import { useUiStore } from '@/store/uiStore';

// Политика размещена на сайте проекта; отдельного документа согласия нет —
// его текст показан на экране под ссылкой
const POLICY_URL = 'https://брендврача.рф/politika-ai';

/**
 * Согласие на обработку данных на шаге регистрации.
 *
 * Ответ `agree-consent` возвращает новое состояние сессии, поэтому переход к
 * следующему шагу выполняет applyResponse, а не локальная навигация.
 */
export function RegistrationConsentScreen() {
  const markConsentGiven = useAuthStore((state) => state.markConsentGiven);
  const toast = useUiStore((state) => state.toast);
  const [saving, setSaving] = useState(false);
  const bridge = getBridge();

  const accept = async () => {
    setSaving(true);
    try {
      // Ручка возвращает только `{ consent: true }`, поэтому состояние сессии
      // из неё не берётся: отмечаем согласие в пользователе локально
      await miniappApi.agreeConsent();
      markConsentGiven();
    } catch {
      toast('Не удалось сохранить согласие. Попробуйте ещё раз.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Screen subtitle="Регистрация" onboarding>
      <Kicker>Условия</Kicker>
      <PageTitle>Согласие на обработку персональных данных</PageTitle>
      <Lead>Для продолжения ознакомьтесь с политикой.</Lead>

      <RowList>
        <Row
          icon={
            <MiniIcon tone="lime">
              <FileLock2 className="h-[17px] w-[17px]" aria-hidden />
            </MiniIcon>
          }
          title="Политика обработки персональных данных"
          subtitle="Открыть документ"
          trailing={<ExternalLink className="h-4 w-4 shrink-0 text-muted" aria-hidden />}
          onClick={() => bridge.openLink(POLICY_URL)}
        />
      </RowList>

      <Panel tone="tint" className="mt-2.5">
        <p className="m-0 text-[12px] leading-[1.45] text-muted">
          Нажимая «Принимаю», я даю согласие ООО «КОДАП ПРО АЙТИ» на обработку моих персональных данных
          (текст сообщений, файлы, голосовые сообщения, технические данные мессенджера) в целях выполнения
          домашних заданий, анализа данных и транскрибации голоса, включая трансграничную передачу
          иностранным контрагентам. Согласие действует до его отзыва.
        </p>
      </Panel>

      <ActionStack>
        <Button variant="primary" block loading={saving} onClick={accept}>
          Принимаю
        </Button>
      </ActionStack>
    </Screen>
  );
}
